<template>
  <div
    class="map"
    ref="map"
  >
    <v-row
      class="map__buttons flex-column"
      dense
    >
      <v-col>
        <v-speed-dial
          v-model="fab"
          direction="right"
          class="map__btn"
        >
          <template v-slot:activator>
            <v-btn
              v-model="fab"
              class="btn--green"
              dark
              fab
              x-small
              :title="
            fab
              ? $i18n.t('dashboard.source.measurement.cancel')
              : !measureType
                ? $i18n.t('dashboard.source.measurement.sketchesAndMeasurements')
                : $i18n.t('dashboard.source.measurement.delete')
          "
              @click="clearMeasurement"
            >
              <v-icon v-if="fab">mdi-close</v-icon>
              <v-icon v-else-if="!fab && measureType">mdi-delete</v-icon>
              <v-icon v-else>mdi-ruler</v-icon>
            </v-btn>
          </template>
          <base-map-button
            title="dashboard.source.measurement.linearSketch"
            @click="addMeasurement('LineString')"
            icon="mdi-chart-line-variant"
          />
          <base-map-button
            title="dashboard.source.measurement.areaSketch"
            @click="addMeasurement('Polygon')"
            icon="mdi-ruler-square"
          />
        </v-speed-dial>
      </v-col>
      <v-col>
        <base-map-button
          title="dashboard.map.zoomToStart"
          @click="zoomToStartView"
          icon="mdi-fullscreen"
        />
      </v-col>

      <v-col>
        <base-map-button
          title="dashboard.map.legend"
          icon="mdi-map-legend"
          @click="openMapLegend"
        />
      </v-col>

      <slot name="map-buttons"></slot>
    </v-row>
    <map-address-search @search="searchAddress" @clear="clearSearchAddress"/>
    <layers-loader :items="layersLoader" />
    <v-row class="map__buttons-right flex-column" dense>
      <v-col v-if="editingLayer">
        <base-map-button
          :title="isUserLayerEditing?'dashboard.map.stopLayerEdit':'dashboard.map.startLayerEdit'"
          :icon="isUserLayerEditing? 'mdi-cancel' : 'mdi-pencil'"
          @click="layerEdit"
        />
      </v-col>
      <v-col v-if="isUserLayerEditing">
        <base-map-button
          :title="$i18n.t('dashboard.map.add' + editLayer.geometry_type)"
          :icon="editingTypeIcon"
          @click="addFeature"
        />
      </v-col>
      <v-col v-if="isUserLayerEditing">
        <base-map-button
          title="dashboard.map.selectFeature"
          icon="mdi-cursor-pointer"
          @click="selectFeature"
        />
      </v-col>
    </v-row>
    <MapLegendDialog
      v-if="isLegendLoaded"
      ref="map_legend"
      :dialog="isLegendActive"
      :isDashboard="true"
      @toggleWMS="toggleWMS"
      @toggleUserLayer="toggleUserLayer"
      @hideLegend="isLegendActive = false"
    >
      <template v-slot:buttons>
        <v-card-actions>
          <div class="flex-grow-1" />
          <v-btn
            color="primary"
            text
            @click="isLegendActive = false"
          >{{ $i18n.t('dialog.confirmation.close') }}</v-btn>
        </v-card-actions>
      </template>
    </MapLegendDialog>
    <AddNewFeatureDialog
      :dialog="isAddNewFeatureActive"
      :fields="editLayer.fields || []"
      @success="saveUserFeature"
      @cancel="cancelUserFeature"
    />
    <FeatureDialog
      :dialog="isFeatureActive"
      :fields="editLayer.fields || []"
      :selectedFeatureProperties="selectedUserFeature?selectedUserFeature.get('properties'):{}"
      @delete="deleteFeature"
      @cancel="hideFeature"
    />
  </div>
</template>

<script>
import { GeoJSON } from 'ol/format';
import Collection from 'ol/Collection';
import Heatmap from 'ol/layer/Heatmap';
import Map from 'ol/Map';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import Draw from 'ol/interaction/Draw';
import VectorSource from 'ol/source/Vector';
import View from 'ol/View';
import { unByKey } from 'ol/Observable.js';
import { getWidth } from 'ol/extent';
import proj4 from 'proj4';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import TileGrid from 'ol/tilegrid/TileGrid';
import { Style, Stroke, Fill, Circle, Text } from 'ol/style';
import Overlay from 'ol/Overlay.js';
import { LineString, Polygon } from 'ol/geom.js';
import { getArea, getLength } from 'ol/sphere.js';
import { basemaps } from '@/models/Basemaps';
import { get as getProjection } from 'ol/proj';
import { getFeatureCentroid } from '@/utils/mapUtils';
import { defaults as defaultControls, FullScreen } from 'ol/control.js';
import { register } from 'ol/proj/proj4';
import { getWMSLayers } from '@/utils/mapUtils';
import searchAddressMixin from '@/mixins/searchAddressMixin';
import layersLoaderMixin from '@/mixins/layersLoaderMixin';
import userLayersMixin from '@/mixins/userLayersMixin';

import vuetify from '@/plugins/vuetify.js';

const vuetifyColors = vuetify.preset.theme.themes.light;

export default {
  mixins: [searchAddressMixin, layersLoaderMixin, userLayersMixin],
  props: {
    stats: {
      type: Array,
      required: true
    }
  },
  components: {
    MapLegendDialog: () => import('../dashboard/MapLegendDialog'),
    MapAddressSearch: () => import('@/components/MapAddressSearch'),
    LayersLoader: () => import('@/components/LayersLoader'),
    AddNewFeatureDialog: () => import('@/components/dashboard/AddNewFeatureDialog'),
    FeatureDialog: () => import('@/components/dashboard/FeatureDialog'),
  },
  data: () => ({
    fab: false,
    map: undefined,
    isLegendActive: false,
    isLegendLoaded: false,
    measureTooltip: undefined,
    measureTooltipElement: undefined,
    measureType: undefined
  }),
  computed: {
    wmsLayers() {
      return this.$store.getters.getWMSLayers;
    },
    currentBasemap() {
      return this.$store.getters.getCurrentBasemap;
    }
  },
  watch: {
    currentBasemap(nV) {
      this.changeBaselayer(nV);
    },
    wmsLayers(nV) {
      this.addWmsLayers(nV);
    },
    stats(newValue) {
      this.updateStats(newValue);
    }
  },
  methods: {
    addWmsLayers(layers) {
      for (let layer of layers) {
        let projExtent;
        if (getProjection(layer.wms_srs).getExtent()) {
          projExtent = getProjection(layer.wms_srs).getExtent();
        } else {
          projExtent = getProjection('EPSG:3857').getExtent();
        }
        let startResolution = getWidth(projExtent) / 512;
        let resolutions = new Array(22);
        for (let i = 0, ii = resolutions.length; i < ii; ++i) {
          resolutions[i] = startResolution / Math.pow(2, i);
        }
        const source = new TileWMS({
          url: `${process.env.VUE_APP_DIVI_BASE_URL}/wms_proxy/${layer.wms_url}`,
          projection: layer.wms_srs,
          params: {
            LAYERS: layer.wms_layers,
            TILED: true,
            VERSION: '1.1.1'
          },
          tileGrid: new TileGrid({
            extent: projExtent,
            resolutions: resolutions,
            tileSize: [512, 512]
          })
        });
        this.map.addLayer(
          new TileLayer({
            name: layer.name_pl,
            visible: false,
            source
          })
        );
        this.addLayersLoaderSource(layer.id, { pl: layer.name_pl, en: layer.name_en }, source);
      }
      this.isLegendLoaded = true;
    },
    formatArea(polygon) {
      var area = getArea(polygon);
      var output;
      if (area > 10000) {
        output = Math.round((area / 10000) * 100) / 100 + ' ' + 'ha';
      } else {
        output = Math.round((area / 10000) * 1000) / 1000 + ' ' + 'ha';
      }
      return output;
    },
    formatLength(line) {
      var length = getLength(line);
      var output;
      if (length > 100) {
        output = Math.round((length / 1000) * 100) / 100 + ' ' + 'km';
      } else {
        output = Math.round(length * 100) / 100 + ' ' + 'm';
      }
      return output;
    },
    addMeasurement(type) {
      const drawLayer = this.getLayerByName('measurement');
      const drawInteraction = this.getInteractionByName('drawMeasurement');

      if (drawInteraction && this.measureType != type) {
        this.map.removeInteraction(drawInteraction);
        this.addMeasurementInteraction(drawLayer, type);
      } else {
        this.addMeasurementInteraction(drawLayer, type);
      }

      this.measureType = type;
      this.getInteractionByName('drawMeasurement').setActive(true);
      this.createMeasureTooltip();
    },
    createMeasureTooltip() {
      this.measureTooltipElement = document.createElement('div');
      this.measureTooltipElement.className = 'tooltip tooltip-measure';
      this.measureTooltip = new Overlay({
        element: this.measureTooltipElement,
        offset: [0, -15],
        positioning: 'bottom-center'
      });
      this.map.addOverlay(this.measureTooltip);
    },
    getInteractionByName(name) {
      return this.map
        .getInteractions()
        .getArray()
        .find(i => i.get('name') == name);
    },
    addMeasurementInteraction(layerName, type) {
      const drawInteraction = new Draw({
        source: layerName.getSource(),
        type: type,

        style: feature => {
          const geometry = feature.getGeometry();
          const styles = [];

          if (type === 'LineString' && geometry instanceof LineString) {
            geometry.forEachSegment((start, end) => {
              const line = new LineString([start, end]);
              styles.push(
                new Style({
                  geometry: line,
                  fill: new Fill({
                    color: `${vuetifyColors.accent}30`
                  }),
                  stroke: new Stroke({
                    color: 'rgba(0, 0, 0, 0.5)',
                    lineDash: [10, 10],
                    width: 2
                  }),
                  text: new Text({
                    text: this.formatLength(line),
                    font: '15px sans-serif',
                    fill: new Fill({ color: 'black' }),
                    placement: 'line',
                    stroke: new Stroke({
                      color: `${vuetifyColors.accent}30`,
                      width: 3
                    }),
                    offsetX: -20,
                    offsetY: 20
                  }),
                  image: new Circle({
                    radius: 5,
                    stroke: new Stroke({
                      color: vuetifyColors.accent
                    }),
                    fill: new Fill({
                      color: `${vuetifyColors.accent}50`
                    })
                  })
                })
              );
            });
          } else {
            styles.push(
              new Style({
                fill: new Fill({
                  color: `${vuetifyColors.accent}30`
                }),
                stroke: new Stroke({
                  color: 'rgba(0, 0, 0, 0.5)',
                  lineDash: [10, 10],
                  width: 2
                }),
                image: new Circle({
                  radius: 5,
                  stroke: new Stroke({
                    color: vuetifyColors.accent
                  }),
                  fill: new Fill({
                    color: `${vuetifyColors.accent}30`
                  })
                })
              })
            );
          }
          return styles;
        }
      });
      drawInteraction.set('name', 'drawMeasurement');
      this.map.addInteraction(drawInteraction);

      let listener;
      drawInteraction.on('drawstart', evt => {
        this.getLayerByName('measurement')
          .getSource()
          .clear();
        // set sketch
        this.sketch = evt.feature;

        listener = this.sketch.getGeometry().on('change', evt => {
          if (evt.target instanceof Polygon) {
            const result = `${this.formatArea(
              evt.target
            )} <br> ${this.formatLength(evt.target)}`;
            this.measureTooltipElement.innerHTML = result;
            this.measureTooltip.setPosition(
              evt.target.getInteriorPoint().getCoordinates()
            );
          } else if (evt.target instanceof LineString) {
            this.measureTooltipElement.innerHTML = this.formatLength(
              evt.target
            );
            this.measureTooltip.setPosition(evt.target.getLastCoordinate());
          }
        });
      });

      drawInteraction.on('drawend', () => {
        this.measureTooltipElement.className = 'tooltip tooltip-static';
        this.measureTooltip.setOffset([0, -7]);
        // unset sketch
        this.sketch = null;
        unByKey(listener);
      });
    },
    clearMeasurement(e) {
      if (!this.fab && this.measureType) {
        e.stopPropagation();
        this.fab = false;
        this.measureType = false;
        this.getInteractionByName('drawMeasurement').setActive(false);
        this.getLayerByName('measurement')
          .getSource()
          .clear();
        this.measureTooltipElement.parentNode.removeChild(
          this.measureTooltipElement
        ); //clear tooltip
      }
    },
    changeBaselayer(layerName) {
      for (let el of this.map.getLayers().getArray()) {
        if (el.get('group') === 'baselayers') {
          if (el.get('name') === layerName) {
            el.setVisible(true);
          } else {
            el.setVisible(false);
          }
        }
      }
    },
    zoomToStartView() {
      this.map.getView().setCenter([2229729.4005892696, 6903852.286423635]);
      this.map.getView().setZoom(6);
      this.$emit('clearSelection');
    },
    toggleWMS(lay) {
      if (this.getLayerByName(lay).getVisible()) {
        this.getLayerByName(lay).setVisible(false);
      } else {
        this.getLayerByName(lay).setVisible(true);
      }
    },
    getLayerByName(name) {
      return this.map
        .getLayers()
        .getArray()
        .find(layer => {
          return layer.get('name') == name;
        });
    },
    openMapLegend() {
      this.isLegendActive = true;
    },
    updateStats(stats) {
      const features = [];
      for (let stat of stats) {
        if (stat.geometry) {
          const geom = stat.geometry;
          const feature = new GeoJSON().readFeature(geom, {
            featureProjection: 'EPSG:3857',
            dataProjection: 'EPSG:4326'
          });
          const featureCentroid = getFeatureCentroid(feature);
          feature.setGeometry(new Point(featureCentroid.geometry.coordinates));
          feature.set('stats', stat.event_count_total);
          features.push(feature);
        }
      }
      this.getLayerByName('stats')
        .getSource()
        .clear();
      this.getLayerByName('stats')
        .getSource()
        .addFeatures(features);
    },
    getWMSLayers
  },
  mounted() {
    this.getWMSLayers().then(r => {
      this.changeBaselayer(this.currentBasemap);
    });
    proj4.defs(
      'EPSG:2180',
      '+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +units=m +no_defs +axis=enu'
    );
    register(proj4);
    getProjection('EPSG:2180').setExtent([
      144907.1658,
      140544.7241,
      877004.007,
      910679.6817
    ]);
    this.map = window.map = new Map({
      target: this.$el,
      view: new View({
        center: [2229729.4005892696, 6903852.286423635],
        zoom: 6
      }),
      controls: defaultControls().extend([new FullScreen()])
    });

    for (let basemap of basemaps) {
      this.map.addLayer(basemap);
    }
    this.map.addLayer(
      new Heatmap({
        name: 'stats',
        source: new VectorSource({
          features: new Collection([]),
          wrapX: false
        }),
        blur: 15,
        radius: 20,
        weight: feature => {
          return feature.get('stats');
        }
      })
    );
    // Warstwa do miarki
    let measurementLayer = new VectorLayer({
      name: 'measurement',
      source: new VectorSource({}),
      style: feature => {
        const geometry = feature.getGeometry();
        const styles = [];

        if (geometry instanceof LineString) {
          geometry.forEachSegment((start, end) => {
            const line = new LineString([start, end]);
            styles.push(
              new Style({
                geometry: line,
                stroke: new Stroke({
                  color: vuetifyColors.accent,
                  width: 2
                }),
                fill: new Fill({
                  color: `${vuetifyColors.accent}30`
                }),
                text: new Text({
                  text: this.formatLength(line),
                  font: '15px sans-serif',
                  fill: new Fill({ color: 'black' }),
                  placement: 'line',
                  stroke: new Stroke({
                    color: `${vuetifyColors.accent}30`,
                    width: 3
                  }),
                  offsetX: -20,
                  offsetY: 20
                }),
                image: new Circle({
                  radius: 5,
                  stroke: new Stroke({
                    color: vuetifyColors.accent
                  }),
                  fill: new Fill({
                    color: `${vuetifyColors.accent}30`
                  })
                })
              })
            );
          });
        } else {
          styles.push(
            new Style({
              stroke: new Stroke({
                color: vuetifyColors.accent,
                width: 2
              }),
              fill: new Fill({
                color: `${vuetifyColors.accent}30`
              })
            })
          );
        }
        return styles;
      }
    });
    this.map.addLayer(measurementLayer);
  }
};
</script>
<style lang="scss">
.ol-zoom {
  top: 2.5em;
}
.map {
  height: 100%;
  width: 100%;
}
.tooltip {
  position: relative;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  color: white;
  padding: 4px 8px;
  opacity: 0.7;
  white-space: nowrap;
}
.tooltip-measure {
  opacity: 1;
  font-weight: bold;
}
.tooltip-static {
  background-color: var(--v-primary-base);
  color: white;
  border: 1px solid white;
}
.tooltip-measure:before,
.tooltip-static:before {
  border-top: 6px solid rgba(0, 0, 0, 0.5);
  border-right: 6px solid transparent;
  border-left: 6px solid transparent;
  content: '';
  position: absolute;
  bottom: -6px;
  margin-left: -7px;
  left: 50%;
}
.tooltip-static:before {
  border-top-color: var(--v-primary-base);
}
.map__buttons {
  position: absolute;
  z-index: 5;
  left: 12px;
  top: 105px;
  max-height: calc(100% - 80px);
  .v-speed-dial__list {
    z-index: 51;
    padding: 0 2px;
  }
}
.map__buttons-right {
  position: absolute;
  z-index: 5;
  right: 12px;
  top: 80px;
  max-height: calc(100% - 80px);
  .v-speed-dial__list {
    z-index: 51;
    padding: 0 2px;
  }
}
</style>